import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ConfigFormData } from 'shared/types/configuration-service';
import { postProjectConfig } from '@/enteties/config/api';
import { rebuildProject } from '@/enteties/project/api';
import { useState } from 'react';
import { projectKeys } from '@/enteties/project/api/projects.query-key';
import { configKeys } from '@/enteties/config/api/config.query-keys';
import { useApiStatusToast } from 'shared/hooks/useApiStatusToast';

export const useApplyConfig = (success: () => void) => {
  const queryClient = useQueryClient();
  const [isPending, setPending] = useState(false);
  const [_, error] = useApiStatusToast();
  const rebuild = useMutation({
    mutationFn: ({ slug }: { slug: string }) => rebuildProject(slug),
    onSuccess: async (_, vars) => {
      await queryClient.invalidateQueries({
        queryKey: projectKeys.allProjects,
      });
      await queryClient.invalidateQueries({
        queryKey: configKeys.projectConfig(vars.slug),
      });
      setPending(false);
      success();
    },
    onError: (e) => {
      setPending(false);
      error({
        description: `${e.message}`,
      });
    },
  });
  const applyConfig = useMutation({
    onMutate: () => {
      setPending(true);
    },
    mutationFn: ({ slug, config }: { slug: string; config: ConfigFormData }) =>
      postProjectConfig(slug, config),
    onSuccess: async (_, variables) => {
      setTimeout(() => rebuild.mutate({ slug: variables.slug }), 3000);
    },
    onError: (e) => {
      setPending(false);
      error({
        description: `${e.message}`,
      });
    },
  });

  return { applyConfig, rebuild, isPending };
};
